import React, { useState, useEffect } from "react";
import "@/App.css";
import "@/premium-theme.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import apiClient from "@/utils/api";
import notificationService from "@/utils/notificationService";

import Login from "@/components/Login";
import Dashboard from "@/components/Dashboard";
import AnalyticsDashboard from "@/components/AnalyticsDashboard";
import StudentList from "@/components/StudentList";
import ProspectList from "@/components/ProspectList";
import StudentDetail from "@/components/StudentDetail";
import UserManagement from "@/components/UserManagement";
import Settings from "@/components/Settings";
import TasksPage from "@/components/TasksPage";
import StudentAppointment from '@/components/StudentAppointment';
import LeadsTileView from "@/components/LeadsTileView";
import AdmissionTileView from "@/components/AdmissionTileView";
import StudentPortal from "@/components/StudentPortal";
import FinanceManagement from "@/components/FinanceManagementNew";
import IncentiveReport from "@/components/IncentiveReport";
import CounselorIncentiveRecords from "@/components/CounselorIncentiveRecords";
import IncentiveDashboard, { PersonalIncentivesEntry } from "@/components/IncentiveDashboard";
import FinancialReports from "@/components/FinancialReports";
import ExpenseManagement from "@/components/ExpenseManagement";
import AdminDashboardUnified from "@/components/AdminDashboardUnified";
import Profile from "@/components/Profile";
import TeamLeaderDashboard from "@/components/TeamLeaderDashboard";
import Marketing from "@/components/Marketing";
import { SessionStatus } from '@/components/SessionStatus';
import { TASK_ROLES, LEAD_ROLES, ADMISSION_ROLES, MANAGEMENT_ROLES, EXCEPTION_ROLES, STAFF_ROLES, homeForRole } from '@/utils/navigation';
import AIInsights from "@/components/AIInsights";
// Campaign Analytics retired Feb 2026 — merged into Sales Intelligence
// Task module removed - import TaskManagement from "@/components/TaskManagementSimple";
// Teams module merged into Users - import TeamManagement from "@/components/TeamManagement";
// Backup module merged into Settings - import BackupManagement from "@/components/BackupManagement";
import Layout from "@/components/Layout";
import { StudentAuthProvider, useStudentAuth } from "@/contexts/StudentAuthContext";
import StudentLogin from "@/components/student-portal/StudentLogin";
import StudentPortalDashboard from "@/components/student-portal/StudentPortalDashboard";

// Auth context
const AuthContext = React.createContext();

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(false);
  const [verification, setVerification] = useState(0);

  useEffect(() => {
    if (window.location.pathname === '/counselling') { setLoading(false); return; }
    const token = localStorage.getItem('token');
    let active = true;
    setAuthError(false);
    if (!token) {
      setLoading(false);
      return;
    }
    setLoading(true);
    // Cached user is never an authority for role gates, including after switching roles.
    apiClient.get('/auth/me', { timeout: 15000 })
      .then(({ data }) => {
        if (!active || localStorage.getItem('token') !== token) return;
        setUser(data);
        localStorage.setItem('user', JSON.stringify(data));
      })
      .catch(error => {
        if (!active) return;
        setUser(null);
        if ([401, 403].includes(error.response?.status)) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
        } else if (localStorage.getItem('token') === token) {
          setAuthError(true);
        }
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [verification]);

  const login = async (email, password) => {
    try {
      const response = await apiClient.post('/auth/login', { email, password });
      const { access_token, user: userData } = response.data;
      
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
      
      toast.success('Welcome back!', {
        description: `Logged in as ${userData.full_name}`
      });
      
      // Initialize push notifications after successful login
      setTimeout(() => {
        initializePushNotifications();
      }, 2000); // Wait 2 seconds before requesting permission
      
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || 'Login failed';
      toast.error('Login Failed', {
        description: message
      });
      return { success: false, error: message };
    }
  };

  // Initialize push notifications
  const initializePushNotifications = async () => {
    try {
      if (!notificationService.isSupported()) {
        console.log('Push notifications not supported');
        return;
      }

      const permissionStatus = notificationService.getPermissionStatus();
      
      if (permissionStatus === 'default') {
        // Ask user if they want to enable notifications
        const enabled = await new Promise((resolve) => {
          toast.info('Enable Notifications', {
            description: 'Get notified about task reminders and important updates',
            action: {
              label: 'Enable',
              onClick: () => resolve(true)
            },
            cancel: {
              label: 'Not now',
              onClick: () => resolve(false)
            },
            duration: 10000
          });
        });

        if (enabled) {
          await notificationService.subscribe();
          toast.success('Notifications Enabled', {
            description: 'You will now receive task reminders'
          });
        }
      } else if (permissionStatus === 'granted') {
        // Auto-subscribe if permission already granted
        const subscribed = await notificationService.isSubscribed();
        if (!subscribed) {
          await notificationService.subscribe();
          console.log('Auto-subscribed to push notifications');
        }
      }
    } catch (error) {
      console.error('Error initializing push notifications:', error);
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    toast.success('Logged out successfully');
  };

  const updateUser = (userData) => {
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, updateUser, loading, authError, retryAuth: () => setVerification(v => v + 1) }}>
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Protected Route component
const ProtectedRoute = ({ children }) => {
  const { user, loading, authError, retryAuth } = useAuth();
  
  if (loading || authError) {
    return <SessionStatus error={authError} onRetry={retryAuth} />;
  }
  
  return user ? children : <Navigate to="/login" replace />;
};

const RoleProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading, authError, retryAuth } = useAuth();
  
  if (loading || authError) {
    return <SessionStatus error={authError} onRetry={retryAuth} />;
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (!allowedRoles.includes(user.role)) {
    // Redirect to dashboard if user doesn't have permission
    return <Navigate to={homeForRole(user.role)} replace />;
  }
  
  return children;
};

// Component to route dashboard based on role
const DashboardRouter = () => {
  const { user } = useAuth();
  const home = homeForRole(user?.role);
  if (home !== '/') return <Navigate to={home} replace />;
  
  // Documentation Team only sees Admissions - redirect to admissions
  if (user?.role === 'documentation_team') {
    return <Navigate to="/admissions" replace />;
  }
  
  // Accountant only sees Admissions + Finance - redirect to admissions (no dashboard)
  if (user?.role === 'accountant') {
    return <Navigate to="/admissions" replace />;
  }
  
  // Admin and Operating Head see AdminDashboardUnified
  if (user?.role === 'admin' || user?.role === 'operating_head') {
    return <AdminDashboardUnified />;
  }
  
  // No fall-through: telecallers never mount the counsellor dashboard.
  return ['counselor', 'team_leader'].includes(user?.role) ? <Dashboard /> : <Navigate to="/profile" replace />;
};

// Student Portal Protected Route
const StudentProtectedRoute = ({ children }) => {
  const { student, loading } = useStudentAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center"><i className="fas fa-spinner fa-spin text-2xl text-gray-400"></i></div>;
  // Check both context state and localStorage (for immediate post-login navigation)
  const hasToken = student || localStorage.getItem('student_token');
  if (!hasToken) return <Navigate to="/student-login" replace />;
  return children;
};

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <StudentAuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/counselling" element={<StudentAppointment />} />
            {/* Student Portal Routes - separate auth */}
            <Route path="/student-login" element={<StudentLogin />} />
            <Route path="/my-portal/*" element={
              <StudentProtectedRoute>
                <StudentPortalDashboard />
              </StudentProtectedRoute>
            } />
            <Route
              path="/*"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Routes>
                      <Route path="/" element={<DashboardRouter />} />
                      <Route path="/prospects" element={<Navigate to="/leads" />} />
                      <Route path="/prospects/:id" element={<RoleProtectedRoute allowedRoles={TASK_ROLES}><StudentDetail /></RoleProtectedRoute>} />
                      {/* Legacy route redirect */}
                      <Route path="/students" element={<Navigate to="/leads" />} />
                      <Route path="/students/:id" element={<RoleProtectedRoute allowedRoles={TASK_ROLES}><StudentDetail /></RoleProtectedRoute>} />
                      <Route path="/leads" element={<RoleProtectedRoute allowedRoles={LEAD_ROLES}><LeadsTileView /></RoleProtectedRoute>} />
                      <Route path="/tasks" element={<RoleProtectedRoute allowedRoles={TASK_ROLES}><TasksPage /></RoleProtectedRoute>} />
                      <Route path="/my-tasks" element={<RoleProtectedRoute allowedRoles={TASK_ROLES}><Navigate to="/tasks" replace /></RoleProtectedRoute>} />
                      <Route path="/student/:id" element={<RoleProtectedRoute allowedRoles={TASK_ROLES}><StudentDetail /></RoleProtectedRoute>} />
                      <Route path="/student-profile/:id" element={<RoleProtectedRoute allowedRoles={TASK_ROLES}><StudentDetail /></RoleProtectedRoute>} />
                      <Route path="/admissions" element={<RoleProtectedRoute allowedRoles={ADMISSION_ROLES}><AdmissionTileView /></RoleProtectedRoute>} />
                      <Route path="/student-portal/:portalId" element={<RoleProtectedRoute allowedRoles={ADMISSION_ROLES}><StudentPortal /></RoleProtectedRoute>} />
                      <Route path="/finance" element={
                        <RoleProtectedRoute allowedRoles={['admin', 'accountant']}>
                          <FinanceManagement />
                        </RoleProtectedRoute>
                      } />
                      <Route path="/finance/settings" element={<Navigate to="/settings" replace />} />
                      <Route path="/finance/incentives" element={
                        <RoleProtectedRoute allowedRoles={['admin', 'accountant']}>
                          <CounselorIncentiveRecords />
                        </RoleProtectedRoute>
                      } />
                      <Route path="/my-incentives" element={<RoleProtectedRoute allowedRoles={['admin', 'accountant', 'operating_head', 'counselor', 'telecaller', 'documentation_team', 'team_leader']}><PersonalIncentivesEntry /></RoleProtectedRoute>} />
                      <Route path="/incentive-dashboard" element={
                        <RoleProtectedRoute allowedRoles={[...LEAD_ROLES, 'telecaller']}><IncentiveDashboard /></RoleProtectedRoute>
                      } />
                      <Route path="/finance/reports" element={
                        <RoleProtectedRoute allowedRoles={['admin', 'accountant']}>
                          <FinancialReports />
                        </RoleProtectedRoute>
                      } />
                      <Route path="/finance/expenses" element={
                        <RoleProtectedRoute allowedRoles={['admin', 'accountant']}>
                          <ExpenseManagement />
                        </RoleProtectedRoute>
                      } />
                      {/* Task module removed - <Route path="/tasks" element={<TaskManagement />} /> */}
                      {/* Analytics & Reports module removed */}
                      {/* Campaign Analytics retired Feb 2026 — merged into Sales Intelligence */}
                      <Route path="/users" element={
                        <RoleProtectedRoute allowedRoles={['admin']}>
                          <UserManagement />
                        </RoleProtectedRoute>
                      } />
                      {/* Teams module merged into Users - redirect old route */}
                      <Route path="/teams" element={<Navigate to="/users" />} />
                      {/* Backup module merged into Settings - redirect old route */}
                      <Route path="/backup" element={<Navigate to="/settings" />} />
                      <Route path="/settings" element={<RoleProtectedRoute allowedRoles={STAFF_ROLES}><Settings /></RoleProtectedRoute>} />
                      <Route path="/ai-insights" element={
                        <RoleProtectedRoute allowedRoles={['admin', 'operating_head']}>
                          <AIInsights />
                        </RoleProtectedRoute>
                      } />
                      <Route path="/marketing" element={
                        <RoleProtectedRoute allowedRoles={['admin', 'operating_head']}>
                          <Marketing />
                        </RoleProtectedRoute>
                      } />
                      <Route path="/exception-queue" element={
                        <RoleProtectedRoute allowedRoles={EXCEPTION_ROLES}>
                          <Navigate to="/tasks?view=exceptions" replace />
                        </RoleProtectedRoute>
                      } />
                      <Route path="/pilot-dashboard" element={
                        <RoleProtectedRoute allowedRoles={EXCEPTION_ROLES}>
                          <Navigate to="/tasks?view=reports" replace />
                        </RoleProtectedRoute>
                      } />
                      <Route path="/my-pilot-tasks" element={
                        <RoleProtectedRoute allowedRoles={TASK_ROLES}>
                          <Navigate to="/tasks" replace />
                        </RoleProtectedRoute>
                      } />
                      <Route path="/profile" element={<Profile />} />
                      <Route path="*" element={<Navigate to="/" />} />
                    </Routes>
                  </Layout>
                </ProtectedRoute>
              }
            />
          </Routes>
        </BrowserRouter>
        </StudentAuthProvider>
        <Toaster 
          position="top-right"
          toastOptions={{
            duration: 4000,
            className: 'font-medium',
          }}
        />
      </AuthProvider>
    </div>
  );
}

export default App;
export { useAuth };